import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CallerStatsTable from '../components/CallerStatsTable';
import { Loader2 } from 'lucide-react';


const CallerStatsScreen = () => {
    const [callers, setCallers] = useState([]);
    const [filteredCallers, setFilteredCallers] = useState([]);
    const [searchText, setSearchText] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const columns = [
        { key: 'caller_id', label: 'ID' },
        { key: 'caller_name', label: 'Name' },
        { key: 'total_calls', label: 'Total Calls' },
        { key: 'successful_calls', label: 'Successful Calls' },
        { key: 'failed_calls', label: 'Failed Calls' },
        { key: 'average_profit_percentage', label: 'Average Profit Percentage (main)' },
        { key: 'sum_profit_percentage', label: 'Sum Profit %' },
        { key: 'sum_loss_percentage', label: 'Sum Loss %' },
        { key: 'avg_profit_per_success', label: 'Avg Profit Per Success' },
        { key: 'avg_loss_per_failure', label: 'Avg Loss Per Failure' },
        { key: 'liability_indicator', label: 'Liability' },
        { key: 'success_rate', label: "Success Rate" },
        { key: 'risk_adjusted_liability', label: "Riska Adjusted Liability" }
    ];

    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                const response = await axios.get('https://solana-callers-bot-production.up.railway.app/api/callerStats');
                setCallers(response.data);
                setFilteredCallers(response.data);
                setLastUpdated(new Date());
                setIsLoading(false);
            } catch (err) {
                setError('Failed to fetch caller stats');
                setIsLoading(false);
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 300000);
        return () => clearInterval(interval);
    }, []);

    const handleSearch = (text, key) => {
        const newSearchText = { ...searchText, [key]: text };
        setSearchText(newSearchText);

        const filtered = callers.filter((caller) =>
            Object.keys(newSearchText).every((k) => {
                if (!newSearchText[k]) return true;
                return String(caller[k])
                    .toLowerCase()
                    .includes(newSearchText[k].toLowerCase());
            })
        );
        setFilteredCallers(filtered);
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.headerTitle}>Caller Statistics</h1>
                {lastUpdated && (
                    <div style={styles.refreshInfo}>
                        Last updated: {lastUpdated.toLocaleTimeString()}
                    </div>
                )}
            </div>

            {error && (
                <span>{error}</span>
            )}

            <div style={styles.searchContainer}>
                <div style={styles.searchGrid}>
                    {columns.map((column) => (
                        <input
                            key={column.key}
                            style={styles.searchInput}
                            placeholder={`Search ${column.label}`}
                            value={searchText[column.key] || ''}
                            onChange={(e) => handleSearch(e.target.value, column.key)}
                        />
                    ))}
                </div>
            </div>

            {isLoading ? (
                <div style={styles.loading}>
                    <Loader2 className="h-6 w-6 animate-spin" />
                    Loading caller stats...
                </div>
            ) : (
                <CallerStatsTable
                    callers={filteredCallers}
                    columns={columns}
                />
            )}
        </div>
    );
};


const styles = {
    container: {
        padding: '20px',
        maxWidth: '100%',
        margin: '0 auto',
        backgroundColor: '#f9fafb',
    },
    header: {
        marginBottom: '24px',
    },
    headerTitle: {
        fontSize: '24px',
        fontWeight: '600',
        color: '#111827',
        marginBottom: '8px',
    },
    searchContainer: {
        backgroundColor: '#ffffff',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        marginBottom: '20px',
        overflowX: 'auto',
    },
    searchGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '12px',
        padding: '8px',
    },
    searchInput: {
        width: '100%',
        padding: '8px 12px',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
        fontSize: '14px',
        color: '#374151',
        backgroundColor: '#f9fafb',
        transition: 'border-color 0.2s',
        ':focus': {
            borderColor: '#3b82f6',
            outline: 'none',
        },
    },
    tableContainer: {
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        overflowX: 'auto',
    },
    table: {
        width: '100%',
        minWidth: '1100px',
        borderCollapse: 'separate',
        borderSpacing: 0,
    },
    headerCell: {
        position: 'sticky',
        top: 0,
        backgroundColor: '#f8fafc',
        color: '#374151',
        fontWeight: '600',
        padding: '14px 16px',
        textAlign: 'left',
        fontSize: '14px',
        borderBottom: '2px solid #e2e8f0',
        whiteSpace: 'nowrap',
    },
    dataCell: {
        padding: '12px 16px',
        fontSize: '14px',
        color: '#4b5563',
        borderBottom: '1px solid #e5e7eb',
        whiteSpace: 'nowrap',
    },
    numericCell: {
        textAlign: 'right',
        fontFamily: 'monospace',
    },
    nameCell: {
        color: '#2563eb',
        fontWeight: '500',
    },
    profitCell: {
        textAlign: 'right',
        fontFamily: 'monospace',
        fontWeight: '500',
    },
    positiveProfit: {
        color: '#059669',
    },
    negativeProfit: {
        color: '#dc2626',
    },
    evenRow: {
        backgroundColor: '#ffffff',
    },
    oddRow: {
        backgroundColor: '#f8fafc',
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px',
        color: '#6b7280',
        gap: '12px',
    },
    refreshInfo: {
        fontSize: '12px',
        color: '#6b7280',
        textAlign: 'right',
        marginTop: '8px',
    },
};

export default CallerStatsScreen;